import { Link } from '@tanstack/react-router';
import { CircleArrowLeft, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';

import { useGetParentFormConfig } from '~entities/parent/api/fetch-parent-form-config';
import { useUpdateParentFormConfig } from '~entities/parent/api/update-parent-form-config';
import { DEFAULT_FORM_FIELD } from '~entities/parent/constants/default-form-fields';
import type { ParentFormConfigField } from '~entities/parent/model/parent-form-config.type';
import { useGetStudioDetailsSyncedTimezone } from '~entities/studios/api/get-studio-details-synced-timezone';
import { useGetWaitListStatus } from '~entities/studios/api/get-wait-list-status';
import { useUpdatePaymentsSettings } from '~entities/studios/api/update-payments-settings';
import { useUpdateWaitListStatus } from '~entities/studios/api/update-wait-list-status';
import { getStudioTimezoneSelectOptions } from '~entities/studios/constants/studio-timezones';

import type { FormConfigField } from '~shared/api/form-config';
import { cn } from '~shared/shadcn/lib/utils';
import { useUnsavedChangesBlocker } from '~shared/hooks/use-unsaved-changes-blocker';
import { Button } from '~shared/shadcn/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '~shared/shadcn/ui/card';
import { Checkbox } from '~shared/shadcn/ui/checkbox';
import { Separator } from '~shared/shadcn/ui/separator';
import { Switch } from '~shared/shadcn/ui/switch';

import { type FormConfigValues } from './types';

/**
 * Converts a given string to kebab case.
 * @param str - The string to convert.
 * @returns The kebab case version of the string.
 */
function toKebabCase(str: string): string {
  return str
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`) // Add hyphen before capital letters and convert to lowercase
    .replace(/--+/g, '-') // Replace multiple hyphens with a single hyphen
    .replace(/^-/, ''); // Remove leading hyphen if present
}

function getFieldKey(field: FormConfigField | ParentFormConfigField) {
  return `${toKebabCase(field.section ?? '')}_${field.fieldId}`;
}

export function RegistrationConfig() {
  const { mutateAsync: updateParentFormConfigAPI, isPending } =
    useUpdateParentFormConfig();
  const { data: parentFormConfig } = useGetParentFormConfig();

  // Studio settings state (sync portal timezone from GHL on load — ENR-3663)
  const { data: studioDetails } = useGetStudioDetailsSyncedTimezone();
  const updateStudioSettingsMutation = useUpdatePaymentsSettings();
  const [timezone, setTimezone] = useState<string>('UTC');

  // Feature settings state
  const { data: featureSettings, isLoading: isLoadingFeatureSettings } =
    useGetWaitListStatus();
  const updateFeatureSettingsMutation = useUpdateWaitListStatus();
  const [allowParentScheduledDrop, setAllowParentScheduledDrop] =
    useState<boolean>(false);
  const [absenceNotificationsEnabled, setAbsenceNotificationsEnabled] =
    useState<boolean>(false);

  const formFields = parentFormConfig?.formFields ?? DEFAULT_FORM_FIELD;

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { isDirty },
  } = useForm<FormConfigValues>({
    defaultValues: {
      fields: {},
    },
  });

  const { dialog: unsavedChangesDialog } = useUnsavedChangesBlocker(isDirty);

  useEffect(() => {
    const defaultValues: Record<string, FormConfigField> = {};
    formFields.forEach((field) => {
      const fieldKey = getFieldKey(field);
      defaultValues[fieldKey] = field as FormConfigField;
    });

    reset({ fields: defaultValues });
  }, [formFields, reset]);

  useEffect(() => {
    if (studioDetails?.timezone) {
      setTimezone(studioDetails.timezone);
    }
  }, [studioDetails]);

  useEffect(() => {
    if (featureSettings) {
      // Explicitly set the value, defaulting to false if not present
      setAllowParentScheduledDrop(
        featureSettings.allowParentScheduledDrop ?? false,
      );
      setAbsenceNotificationsEnabled(
        featureSettings.absenceNotificationsEnabled ?? false,
      );
    }
  }, [featureSettings]);

  const sections = Array.from(
    new Set(formFields.map((field) => field.section)),
  );

  const onSubmit = async (data: FormConfigValues) => {
    // convert to Array<FormField>
    const updatedFields = Object.values(data.fields);

    try {
      await updateParentFormConfigAPI({
        formFields: updatedFields as Array<ParentFormConfigField>,
        isActive: true,
        entity: 'parent',
      });
    } catch (error) {
      console.error('Failed to update parent form config:', error);
    }
  };

  const handleSaveStudioSettings = () => {
    updateStudioSettingsMutation.mutate(
      { timezone },
      {
        onSuccess: () => {
          toast.success('Studio settings have been updated.');
        },
        onError: () => {
          toast.error('Failed to update studio settings. Please try again.');
        },
      },
    );
  };

  const handleSaveFeatureSettings = () => {
    if (isLoadingFeatureSettings || !featureSettings) {
      return;
    }

    updateFeatureSettingsMutation.mutate(
      {
        allowParentScheduledDrop,
        absenceNotificationsEnabled,
      },
      {
        onSuccess: () => {
          toast.success('Feature settings have been updated.');
        },
        onError: () => {
          toast.error('Failed to update feature settings. Please try again.');
        },
      },
    );
  };

  return (
    <div className='p-4'>
      <Link to='/dashboard' className='mb-8 flex items-center gap-2'>
        <CircleArrowLeft className='size-6' />
        <span className='text-lg font-semibold'>Back to Dashboard</span>
      </Link>
      <Card className='mx-auto max-w-4xl'>
        <CardHeader>
          <CardTitle>Parent Registration Form Settings</CardTitle>
          <CardDescription>
            Customize the fields shown in the parent registration form
          </CardDescription>
        </CardHeader>
        <CardContent className='space-y-6'>
          <form onSubmit={handleSubmit(onSubmit)}>
            {sections.map((section) => (
              <div key={section} className='mb-4 space-y-4'>
                <div>
                  <h3 className='text-lg font-semibold'>{section}</h3>
                  <Separator className='my-2' />
                </div>
                <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
                  {formFields
                    .filter((field) => field.section === section)
                    .sort((a, b) => a.order - b.order)
                    .map((formField) => {
                      const fieldKey = getFieldKey(formField);
                      const isVisible = watch(`fields.${fieldKey}.visible`);
                      const disabled =
                        (formField.fieldId === 'firstName' &&
                          formField.section !== 'Emergency Contacts') ||
                        (formField.fieldId === 'lastName' &&
                          formField.section !== 'Emergency Contacts') ||
                        (formField.fieldId === 'email' &&
                          formField.section === 'General Info') ||
                        (formField.fieldId === 'dob' &&
                          formField.section === 'Students') ||
                        (formField.fieldId === 'enrollments' &&
                          formField.section === 'Students');
                      return (
                        <div
                          key={fieldKey}
                          className={cn(
                            'flex flex-col space-y-2',
                            disabled && 'pointer-events-none opacity-30',
                          )}
                        >
                          <Controller
                            name={`fields.${fieldKey}.visible`}
                            control={control}
                            render={({ field }) => (
                              <div
                                className={cn(
                                  'flex select-none items-center justify-between',
                                  disabled && 'pointer-events-none',
                                )}
                              >
                                <label
                                  htmlFor={fieldKey}
                                  className={cn(
                                    'text-sm font-medium',
                                    !field.value && 'text-muted-foreground/50',
                                  )}
                                >
                                  {formField.label}
                                </label>
                                <Switch
                                  id={fieldKey}
                                  checked={field.value}
                                  disabled={disabled}
                                  onCheckedChange={(checked) => {
                                    field.onChange(checked);
                                    if (!checked) {
                                      setValue(
                                        `fields.${fieldKey}.required`,
                                        false,
                                      );
                                    }
                                  }}
                                />
                              </div>
                            )}
                          />
                          <Controller
                            name={`fields.${fieldKey}.required`}
                            control={control}
                            render={({ field }) => (
                              <div
                                className={cn(
                                  'flex select-none items-center space-x-2 py-1',
                                  !isVisible && 'opacity-50',
                                  disabled && 'pointer-events-none',
                                )}
                              >
                                <Checkbox
                                  id={`${fieldKey}-required`}
                                  checked={field.value}
                                  disabled={disabled || !isVisible}
                                  onCheckedChange={(checked) => {
                                    field.onChange(checked);
                                  }}
                                />
                                <label
                                  htmlFor={`${fieldKey}-required`}
                                  className={cn(
                                    'text-sm text-muted-foreground',
                                    (!isVisible || disabled) &&
                                      'cursor-not-allowed text-opacity-50',
                                  )}
                                >
                                  Required field
                                </label>
                              </div>
                            )}
                          />
                        </div>
                      );
                    })}
                </div>
              </div>
            ))}
            <div className='flex justify-end pt-4'>
              <Button type='submit' disabled={isPending}>
                {isPending ? (
                  <>
                    <Loader2 className='mr-2 size-4 animate-spin' />
                    Saving Settings
                  </>
                ) : (
                  'Save Settings'
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card className='mx-auto mt-8 max-w-4xl'>
        <CardHeader>
          <CardTitle>Studio Settings</CardTitle>
          <CardDescription>
            Configure your studio&apos;s timezone and other preferences
          </CardDescription>
        </CardHeader>
        <CardContent className='space-y-6'>
          <div className='space-y-4'>
            <div className='space-y-2'>
              <h3 className='text-lg font-semibold'>Timezone</h3>
              <p className='text-sm text-muted-foreground'>
                Matches your GHL account timezone for scheduling and invoicing
              </p>
            </div>

            <div className='space-y-3'>
              <div className='rounded-lg border bg-card p-5'>
                <div className='space-y-3'>
                  <div className='space-y-1'>
                    <label className='text-sm font-medium'>
                      Studio Timezone
                    </label>
                    <p className='text-xs text-muted-foreground'>
                      Auto-synced from your GHL location. Used for all date and
                      time calculations. You can override and save if needed.
                    </p>
                  </div>

                  <select
                    value={timezone}
                    onChange={(e) => {
                      setTimezone(e.target.value);
                    }}
                    className='flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
                  >
                    {getStudioTimezoneSelectOptions(timezone).map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div className='flex justify-end pt-4'>
            <Button
              onClick={handleSaveStudioSettings}
              disabled={updateStudioSettingsMutation.isPending}
            >
              {updateStudioSettingsMutation.isPending ? (
                <>
                  <Loader2 className='mr-2 size-4 animate-spin' />
                  Saving Settings
                </>
              ) : (
                'Save Studio Settings'
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className='mx-auto mt-8 max-w-4xl'>
        <CardHeader>
          <CardTitle>Feature Settings</CardTitle>
          <CardDescription>
            Enable or disable specific features for your studio
          </CardDescription>
        </CardHeader>
        <CardContent className='space-y-6'>
          <div className='space-y-4'>
            <div className='rounded-lg border bg-card p-5'>
              <div className='flex items-center justify-between'>
                <div className='space-y-1'>
                  <label className='text-sm font-medium'>
                    Scheduled Drops Settings
                  </label>
                  <p className='text-xs text-muted-foreground'>
                    Allow parents to schedule drops for their students from the
                    parent portal
                  </p>
                </div>
                <Switch
                  checked={allowParentScheduledDrop}
                  onCheckedChange={setAllowParentScheduledDrop}
                  disabled={isLoadingFeatureSettings}
                />
              </div>
            </div>

            <div className='rounded-lg border bg-card p-5'>
              <div className='flex items-center justify-between'>
                <div className='space-y-1'>
                  <label className='text-sm font-medium'>
                    Absence Notifications
                  </label>
                  <p className='text-xs text-muted-foreground'>
                    Send email to parents when a student is marked absent
                  </p>
                </div>
                <Switch
                  checked={absenceNotificationsEnabled}
                  onCheckedChange={setAbsenceNotificationsEnabled}
                  disabled={isLoadingFeatureSettings}
                />
              </div>
            </div>
          </div>

          <div className='flex justify-end pt-4'>
            <Button
              onClick={handleSaveFeatureSettings}
              disabled={
                isLoadingFeatureSettings ||
                !featureSettings ||
                updateFeatureSettingsMutation.isPending
              }
            >
              {updateFeatureSettingsMutation.isPending ? (
                <>
                  <Loader2 className='mr-2 size-4 animate-spin' />
                  Saving Settings
                </>
              ) : (
                'Save Settings'
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {unsavedChangesDialog}
    </div>
  );
}
